import * as yup from 'yup';
import { LEARN_FILMS, LEARN_RETIRED_VIDEOS } from '../learn/learnLibrary';

/**
 * E34 · Worksheets — the both-ends input codecs of the worksheet mutations (block 2a; plan 03 §2.1 #3):
 * the portal's Builder and the phone's form validate with the SAME schema before sending, the API runs
 * it again as the shield's inputRule. SHAPE only — lengths, the closed vocabularies, and the video link
 * ("The link is checked both ends by the shared Yup schema (youtube.com · youtu.be · vimeo.com ·
 * player.vimeo.com; a Learn id from the catalogue)"). The business rules (a template with nothing in
 * it, two fields with one id, a scoring rule over a text field, a signed journal; the answers against
 * the pinned version) are the deciders', where they are binding (`@cc/domain` homework/worksheets.ts).
 *
 * The vocabularies below MUST equal `@cc/domain`'s (`WORKSHEET_FIELD_TYPES`, `WORKSHEET_VIDEO_SOURCES`,
 * `WORKSHEET_TEMPLATE_CATEGORIES`, `WORKSHEET_SIGNATURE_METHODS`) — `@cc/domain` depends on this package,
 * so the lists live twice and a domain spec (`homework/specs/wire.spec.ts`) pins them equal.
 *
 * ABSENT on purpose: `practiceId`, `performer`, `version` (the server's — the caller, the caller's
 * practice, the next version), a Define's `templateId` (minted server-side), the signature's
 * `ipAddress` / `userAgent` (server-stamped).
 */
export const WORKSHEET_FIELD_TYPE_VALUES = [
  'text',
  'longText',
  'number',
  'scale',
  'singleChoice',
  'multiChoice',
  'date',
  'checkbox',
] as const;
export const WORKSHEET_VIDEO_SOURCE_VALUES = ['learn', 'youtube', 'vimeo'] as const;
/** F-197 (Q-lib-cat-2 A, 2026-09-30): five purpose categories; `homework` stays valid for recorded history. */
export const WORKSHEET_CATEGORY_VALUES = [
  'sessions',
  'problems',
  'selfEsteem',
  'emotions',
  'relationships',
  'homework',
  'journal',
  'intake',
  'consent',
  'lesson',
] as const;
export const WORKSHEET_SIGNATURE_METHOD_VALUES = ['typedName'] as const;
/** The template languages — the app's two (a section's Learn video comes from that catalogue). */
export const WORKSHEET_LANGUAGE_VALUES = ['en', 'ru'] as const;
/** What a saved import may say it was (Q-e34-17 A: the provenance only). */
export const WORKSHEET_IMPORT_FORMAT_VALUES = ['pdf', 'docx', 'image'] as const;
/**
 * E34 block 5b · the import's limit (Q-e34-15 A: "up to 20 MB in all" — the inline request limit), checked by
 * the API before any Vertex call and shown by the portal before upload. MUST equal `@cc/domain`'s
 * `WORKSHEET_IMPORT_MAX_BYTES` (wire.spec pins it).
 */
export const WORKSHEET_IMPORT_MAX_BYTES = 20 * 1024 * 1024;
/** How an import can end without a draft (the portal localizes `worksheets.import.errors.<CODE>`). */
export const WORKSHEET_IMPORT_REFUSAL_CODE_VALUES = [
  'WORKSHEET_IMPORT_TOO_LARGE',
  'WORKSHEET_IMPORT_UNREADABLE',
  'WORKSHEET_IMPORT_FILLED',
  'WORKSHEET_IMPORT_BUSY',
] as const;
/** The files the import reads: a PDF, a Word file, photos / scans (JPEG, PNG, WEBP, HEIC). */
export const WORKSHEET_IMPORT_ACCEPT = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export const WORKSHEET_IMPORT_EXTENSIONS = ['.pdf', '.docx', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'] as const;

/**
 * The CopingCard Learn videos a section may name (E56: the films table — every film with a YouTube id, in either
 * language — plus the seven retired English clips, which an existing worksheet may still carry and which keep
 * playing, Q-learn-7 A). The portal's PICKER offers only the new films (`learnVideosOf`); this is what SAVE accepts.
 */
export const LEARN_VIDEO_IDS: readonly string[] = [
  ...new Set(
    [
      ...LEARN_FILMS.flatMap((f) => [f.en?.id, f.ru?.id]),
      ...LEARN_RETIRED_VIDEOS.map((v) => v.id),
    ].filter((id): id is string => typeof id === 'string' && id.length > 0),
  ),
];

const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'];
const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'];

/** The host of an https URL, lower-cased; null for anything else (no `URL` global in this package). */
const hostOf = (url: string): string | null => {
  const m = /^https:\/\/([A-Za-z0-9.-]+)(?::\d+)?(?:[/?#]|$)/.exec(url.trim());
  return m ? m[1].toLowerCase() : null;
};

/**
 * Is `ref` a video the section may carry — a Learn catalogue id, or an https YouTube / Vimeo link? The
 * Builder's video card refuses anything else before Save (worksheets.builder.video.unsupported).
 */
export const isSupportedWorksheetVideo = (
  source: string | null | undefined,
  ref: string | null | undefined,
): boolean => {
  if (!ref) return false;
  if (source === 'learn') return LEARN_VIDEO_IDS.includes(ref);
  const host = hostOf(ref);
  if (!host) return false;
  if (source === 'youtube') return YOUTUBE_HOSTS.includes(host);
  if (source === 'vimeo') return VIMEO_HOSTS.includes(host);
  return false;
};

/** The source a pasted link is from (`youtube` · `vimeo`), or null when it is neither. */
export const worksheetVideoSourceOf = (
  url: string | null | undefined,
): 'youtube' | 'vimeo' | null => {
  const host = url ? hostOf(url.trim()) : null;
  if (host && YOUTUBE_HOSTS.includes(host)) return 'youtube';
  if (host && VIMEO_HOSTS.includes(host)) return 'vimeo';
  return null;
};

const id = (max = 64) =>
  yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9_-]+$/)
    .min(1)
    .max(max);

export const worksheetVideoSchema = yup
  .object({
    source: yup
      .string()
      .oneOf([...WORKSHEET_VIDEO_SOURCE_VALUES])
      .required(),
    ref: yup.string().trim().min(1).max(2048).required(),
    title: yup.string().trim().min(1).max(200).required(),
    duration: yup.number().integer().min(0).nullable(),
  })
  .test(
    'worksheet-video-supported',
    'worksheets.builder.video.unsupported',
    (v) => !v || isSupportedWorksheetVideo(v.source, v.ref),
  )
  .default(undefined)
  .nullable();

const CHOICE_TYPES = ['singleChoice', 'multiChoice'];

export const worksheetFieldSchema = yup
  .object({
    fieldId: id().required(),
    type: yup
      .string()
      .oneOf([...WORKSHEET_FIELD_TYPE_VALUES])
      .required(),
    label: yup.string().trim().min(1).max(1000).required(),
    required: yup.boolean().nullable(),
    options: yup
      .array()
      .of(yup.string().trim().min(1).max(200).required())
      .max(50)
      .nullable(),
    min: yup.number().nullable(),
    max: yup.number().nullable(),
  })
  // a choice needs options to choose from; a scale needs its two ends, low below high
  .test('worksheet-field-options', 'worksheets.builder.errors.options', (f) =>
    !f || !CHOICE_TYPES.includes(f.type as string) || (f.options?.length ?? 0) >= 1,
  )
  .test('worksheet-field-scale', 'worksheets.builder.errors.scale', (f) => {
    if (!f || f.type !== 'scale') return true;
    return (
      typeof f.min === 'number' && typeof f.max === 'number' && f.min < f.max
    );
  });

export const worksheetSectionSchema = yup.object({
  sectionId: id().required(),
  title: yup.string().trim().max(200).nullable(),
  text: yup.string().max(20000).nullable(),
  video: worksheetVideoSchema,
  fields: yup.array().of(worksheetFieldSchema.required()).max(100).nullable(),
});

export const worksheetScoringSchema = yup
  .object({
    kind: yup.string().oneOf(['sum']).required(),
    fields: yup.array().of(id().required()).min(1).max(100).required(),
    bands: yup
      .array()
      .of(
        yup.object({
          min: yup.number().required(),
          max: yup.number().required(),
          label: yup.string().trim().min(1).max(100).required(),
        }),
      )
      .max(20)
      .required(),
  })
  .default(undefined)
  .nullable();

const worksheetSections = yup
  .array()
  .of(worksheetSectionSchema.required())
  .min(1)
  .max(50)
  .required()
  .test(
    'worksheet-sections-unique',
    'worksheets.builder.errors.sectionIds',
    (sections) => {
      const ids = (sections ?? []).map((s) => s?.sectionId);
      return new Set(ids).size === ids.length;
    },
  );

const templateContent = {
  name: yup.string().trim().min(1).max(200).required(),
  category: yup
    .string()
    .oneOf([...WORKSHEET_CATEGORY_VALUES])
    .required(),
  language: yup
    .string()
    .oneOf([...WORKSHEET_LANGUAGE_VALUES])
    .required(),
  sections: worksheetSections,
  scoring: worksheetScoringSchema,
  signatureRequired: yup.boolean().required(),
  repeatable: yup.boolean().required(),
};

/** A library template's id — `lib-…`, never a practice's `wt-…` (the member names it). */
export const LIBRARY_TEMPLATE_ID = /^lib-[a-z0-9][a-z0-9-]{1,80}$/;

export const publishWorksheetTemplateInputSchema = yup.object({
  templateId: yup.string().trim().matches(LIBRARY_TEMPLATE_ID).required(),
  ...templateContent,
});

export const defineWorksheetTemplateInputSchema = yup.object({
  ...templateContent,
  copiedFrom: yup.string().trim().max(128).nullable(),
  // Q-ugc-2 A — optional on a save (only ImportWorksheetTemplate requires it)
  rightsConfirmed: yup.boolean(),
  importedFrom: yup
    .object({
      format: yup
        .string()
        .oneOf([...WORKSHEET_IMPORT_FORMAT_VALUES])
        .required(),
      // the PDF's page count or the number of photos; null for a Word file (block 5b)
      pages: yup.number().integer().min(1).max(1000).nullable(),
    })
    .default(undefined)
    .nullable(),
});

/**
 * E34 block 5b · ImportWorksheetTemplate — the upload, as the portal sends it: each file's name, type and
 * size, and its bytes (base64) — held in the request's memory only, never stored (Q-e34-17 A). `data` is
 * absent when the portal already knows the upload is over the limit: the API still RECORDS the request and
 * its refusal (every upload is counted — Q-e34-23 A) without the bytes it would not send anyway. SHAPE only:
 * what the bytes ARE (the format, the page count) the API reads from the bytes themselves.
 */
export const importWorksheetTemplateInputSchema = yup.object({
  files: yup
    .array()
    .of(
      yup.object({
        name: yup.string().trim().min(1).max(300).required(),
        mimeType: yup.string().trim().max(200).nullable(),
        size: yup.number().integer().min(0).required(),
        data: yup.string().nullable(),
      }),
    )
    .min(1)
    .max(30)
    .required(),
  // Q-ugc-1 A — checked before the file is read
  rightsConfirmed: yup.boolean(),
});

export const reviseWorksheetTemplateInputSchema = yup.object({
  templateId: yup.string().trim().min(1).max(128).required(),
  ...templateContent,
  rightsConfirmed: yup.boolean(),
});

export const retireWorksheetTemplateInputSchema = yup.object({
  templateId: yup.string().trim().min(1).max(128).required(),
  reason: yup.string().trim().min(1).max(1000).required(),
});

/** One answer: text · a number · the options chosen · a tick · null (a date is `YYYY-MM-DD` text). */
const isAnswerValue = (v: unknown): boolean =>
  v === null ||
  typeof v === 'boolean' ||
  (typeof v === 'number' && Number.isFinite(v)) ||
  (typeof v === 'string' && v.length <= 20000) ||
  (Array.isArray(v) &&
    v.length <= 50 &&
    v.every((x) => typeof x === 'string' && x.length <= 200));

export const worksheetAnswersSchema = yup
  .mixed<Record<string, unknown>>()
  .test('worksheet-answers', 'worksheets.form.errors.answers', (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    const entries = Object.entries(v as Record<string, unknown>);
    return (
      entries.length <= 500 &&
      entries.every(
        ([k, x]) => /^[A-Za-z0-9_-]{1,64}$/.test(k) && isAnswerValue(x),
      )
    );
  })
  .required();

export const submitWorksheetInputSchema = yup.object({
  assignmentId: yup.string().trim().min(1).max(128).required(),
  submissionId: yup.string().trim().min(1).max(128).nullable(),
  templateId: yup.string().trim().max(128).nullable(),
  templateVersion: yup.number().integer().min(1).nullable(),
  answers: worksheetAnswersSchema,
});

export const signWorksheetInputSchema = yup.object({
  submissionId: yup.string().trim().min(1).max(128).required(),
  signerName: yup.string().trim().min(1).max(200).required(),
  agreed: yup.boolean().required(),
  signatureMethod: yup
    .string()
    .oneOf([...WORKSHEET_SIGNATURE_METHOD_VALUES, null])
    .nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const publishWorksheetTemplateArgsSchema = yup.object({
  input: publishWorksheetTemplateInputSchema.required(),
});
export const defineWorksheetTemplateArgsSchema = yup.object({
  input: defineWorksheetTemplateInputSchema.required(),
});
export const reviseWorksheetTemplateArgsSchema = yup.object({
  input: reviseWorksheetTemplateInputSchema.required(),
});
export const retireWorksheetTemplateArgsSchema = yup.object({
  input: retireWorksheetTemplateInputSchema.required(),
});
export const submitWorksheetArgsSchema = yup.object({
  input: submitWorksheetInputSchema.required(),
});
export const signWorksheetArgsSchema = yup.object({
  input: signWorksheetInputSchema.required(),
});
export const importWorksheetTemplateArgsSchema = yup.object({
  input: importWorksheetTemplateInputSchema.required(),
});

export const worksheetValidators = {
  publishWorksheetTemplateInputSchema,
  defineWorksheetTemplateInputSchema,
  reviseWorksheetTemplateInputSchema,
  retireWorksheetTemplateInputSchema,
  submitWorksheetInputSchema,
  signWorksheetInputSchema,
  publishWorksheetTemplateArgsSchema,
  defineWorksheetTemplateArgsSchema,
  reviseWorksheetTemplateArgsSchema,
  retireWorksheetTemplateArgsSchema,
  submitWorksheetArgsSchema,
  signWorksheetArgsSchema,
  importWorksheetTemplateInputSchema,
  importWorksheetTemplateArgsSchema,
};
